// rover.ts — a body on the ground.
//
// The Sampler quotes 12.9 degrees mean slope and 6.22 m rms roughness. Both are
// true, both are measured off the same height field the shader displaces, and
// neither means anything to a person reading them from six hundred metres up.
// This is that panel made physical: a known-size object that has to cross the
// ground, so the numbers stop being numbers.
//
// Three jobs, in this order:
//
//   1. SCALE. Curiosity is 3.0 m long and 2.2 m tall, and those are the
//      dimensions used here. Nothing makes a landscape legible like an object
//      whose size you already know sitting in it.
//   2. A CAMERA AT THE RIGHT HEIGHT. The mast puts an eye 2 m above the
//      regolith, which is the view the whole terrain lane has been circling —
//      and the view the rock scatter was built for.
//   3. A PROBE. Six wheels each sample the surface independently, so a slope
//      you would have read past becomes a slope you climb slowly, and roughness
//      becomes the thing making the chassis work.
//
// ── What this is and is not ──────────────────────────────────────────────────
//
// KINEMATIC, not dynamic. The rover advances by throttle and steering, its
// wheels are placed on the terrain, and the chassis takes the attitude of the
// plane those wheels describe. There is no rigid body, no contact solver and no
// integration of forces. That is a deliberate trade: a real solver's failure
// modes are launching the vehicle into orbit off a texel edge, and a survey
// instrument that occasionally throws its rover at the sky is worse than one
// that corners a little too politely.
//
// The rocker-bogie is real geometry, though. Six wheels on two rockers with a
// bogie each is what lets a Mars rover keep all six down on terrain like this,
// and it is the silhouette that says "Mars" before any texture does.

import * as THREE from 'three';
import { wrapRad } from '../math/angles.ts';
import type { PlotField, SubPlot, DataTier } from './plotField';
import { insideHexZ } from './plotField.ts';
import { OBSTACLE_STRIDE, type RockField, type RockPush } from './rockField.ts';
import { TEST_SWITCHES } from '../urlFlags.ts';

const _v = new THREE.Vector3();
const _inv = new THREE.Matrix4();
const _up = new THREE.Vector3(0, 1, 0);
const _push: RockPush = { x: 0, z: 0, depth: 0 };

/**
 * Credit for the imported model, which is a LICENCE TERM and not a courtesy.
 *
 * CC-BY-4.0 permits commercial use and requires the author be credited wherever
 * the work is shared. Keeping the string next to the code that loads the asset
 * means the obligation travels with the thing it applies to, rather than living
 * in a file somebody has to remember to read.
 */
/**
 * ?roverProbe=1 hands the live rover to the console.
 *
 * The wheel rigging has twice shipped as a silent no-op, and both times the
 * only thing that could tell the difference between "working" and "doing
 * nothing" was measuring the geometry itself. tools/preview/rover-probe.mjs
 * does that, and this is the door it comes in by. Off by default, one
 * URLSearchParams read at module load, nothing in the frame loop.
 */
const ROVER_PROBE = typeof location !== 'undefined' && TEST_SWITCHES
  && new URLSearchParams(location.search).has('roverProbe');

export const CURIOSITY_CREDIT = {
  title: 'Mars Curiosity Rover',
  author: 'johnalexandro',
  authorUrl: 'https://sketchfab.com/johnalexandro',
  source: 'https://sketchfab.com/3d-models/mars-curiosity-rover-9a2810f69193403d9b1273d55d5e032c',
  licence: 'CC-BY-4.0',
  licenceUrl: 'http://creativecommons.org/licenses/by/4.0/',
  file: '/art/atlas/models/curiosity-johnalexandro.glb',
} as const;

/** Curiosity's numbers, because the point is that the size is known. */
export const ROVER = {
  lengthM: 3.0,
  widthM: 2.7,
  /** Deck height above the ground, before the mast. */
  deckM: 1.1,
  /** Mast camera height — the eye. */
  mastM: 2.0,
  wheelRadiusM: 0.25,
  wheelWidthM: 0.40,
  /** Fore / mid / aft wheel stations, metres from the body centre. */
  axleZ: [1.25, 0.0, -1.25],
  halfTrack: 1.15,
  /**
   * Top speed, m/s.
   *
   * Curiosity does about 0.04 m/s. This does 4, which is a hundred times too
   * fast and not an accident: the rover is here to let a person read a
   * kilometre of ground in under a minute, and a truthful 0.04 would make that
   * a twenty-five minute drive. The slope model below is honest; the clock is
   * not, and this comment is the disclosure.
   */
  maxSpeed: 4.0,
  /** Slope beyond which the wheels lose the ground, degrees. Curiosity's
   *  operational limit is around 30; its tested limit is nearer 45. */
  slopeLimitDeg: 32,
  turnRate: 1.1,
} as const;

/** How much larger than life the imported model is drawn. See setSkin(). */
export const CURIOSITY_MODEL_SCALE = 3.0;

export interface RoverState {
  /** Plot-local metres. */
  x: number;
  z: number;
  /** Radians, 0 = +Z. */
  yaw: number;
  speed: number;
  /** True while any wheel is on ground too steep to hold. */
  slipping: boolean;
  /** Slope under the body right now, degrees — the number you are feeling. */
  slopeDeg: number;
}

export interface RoverControls {
  throttle: number;  // -1 .. 1
  steer: number;     // -1 .. 1
  brake: boolean;
}

/**
 * Find the six wheels of an imported rover without being told where they are.
 *
 * Every earlier attempt at this hardcoded the stations as fractions of the
 * bounding box — 0.79, 0.44, 0.13 along the vehicle — measured once by hand off
 * the raw vertex arrays. Those fractions did not survive the asset's own node
 * transforms: in the space the extraction actually runs in, the front stations
 * sit at 0.68, not 0.79. The boxes landed a tenth of a vehicle length off the
 * front wheels and swallowed suspension instead, and every downstream fit was
 * then fitting a circle to a bracket.
 *
 * The fix is to stop measuring by hand. A rover's wheels have one property
 * nothing else on the vehicle has: THEY TOUCH THE GROUND. So the model is
 * rasterised into a plan view holding the lowest geometry over each cell, the
 * cells sitting on the floor are labelled into connected blobs, and the six
 * largest blobs are the six contact patches. That is a measurement, and it
 * re-measures itself if the asset is ever re-exported at another scale, in
 * another orientation, or by another author.
 */
interface Station {
  /** Contact patch centre, across the vehicle and along it. */
  x: number;
  z: number;
  /** Lowest geometry in the patch — the bottom of the tyre. */
  contactY: number;
  /** Patch width across the vehicle: the width of the tyre. */
  halfX: number;
}

function findStations(
  meshes: THREE.Mesh[],
  rels: Map<THREE.Mesh, THREE.Matrix4>,
  box: THREE.Box3,
  size: THREE.Vector3,
): Station[] {
  // Square-ish cells, so a blob's shape means the same thing in both axes.
  const NX = 72;
  const cell = size.x / NX;
  const NZ = Math.max(8, Math.round(size.z / cell));
  const lo = new Float64Array(NX * NZ).fill(Infinity);
  const tmp = new THREE.Vector3();

  for (const m of meshes) {
    const rel = rels.get(m)!;
    const pa = m.geometry.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < pa.count; i++) {
      tmp.fromBufferAttribute(pa, i).applyMatrix4(rel);
      const ix = Math.min(NX - 1, Math.max(0, Math.floor(((tmp.x - box.min.x) / size.x) * NX)));
      const iz = Math.min(NZ - 1, Math.max(0, Math.floor(((tmp.z - box.min.z) / size.z) * NZ)));
      const k = iz * NX + ix;
      if (tmp.y < lo[k]!) lo[k] = tmp.y;
    }
  }

  // On the floor, within a tolerance of a fraction of the vehicle's height.
  const floor = box.min.y + size.y * 0.10;
  const seen = new Uint8Array(NX * NZ);
  const blobs: Array<{ n: number; sx: number; sz: number; y: number; x0: number; x1: number }> = [];
  const queue: number[] = [];

  for (let start = 0; start < NX * NZ; start++) {
    if (seen[start] || !(lo[start]! <= floor)) continue;
    seen[start] = 1;
    queue.length = 0;
    queue.push(start);
    let n = 0;
    let sx = 0;
    let sz = 0;
    let y = Infinity;
    let x0 = Infinity;
    let x1 = -Infinity;
    while (queue.length) {
      const k = queue.pop()!;
      const ix = k % NX;
      const iz = (k - ix) / NX;
      n++;
      sx += ix; sz += iz; if (lo[k]! < y) y = lo[k]!;
      if (ix < x0) x0 = ix;
      if (ix > x1) x1 = ix;
      const nb = [ix > 0 ? k - 1 : -1, ix < NX - 1 ? k + 1 : -1,
                  iz > 0 ? k - NX : -1, iz < NZ - 1 ? k + NX : -1];
      for (const j of nb) {
        if (j < 0 || seen[j] || !(lo[j]! <= floor)) continue;
        seen[j] = 1;
        queue.push(j);
      }
    }
    blobs.push({ n, sx, sz, y, x0, x1 });
  }

  // Six wheels: the six biggest things on the ground. Anything else touching
  // the floor is small — a dropped bolt, a stray vertex, the arm's stowed tip.
  blobs.sort((p, q) => q.n - p.n);
  if (blobs.length < 6) return [];
  const six = blobs.slice(0, 6);

  const toX = (ix: number): number => box.min.x + ((ix + 0.5) / NX) * size.x;
  const toZ = (iz: number): number => box.min.z + ((iz + 0.5) / NZ) * size.z;
  const st = six.map((bl) => ({
    x: toX(bl.sx / bl.n),
    z: toZ(bl.sz / bl.n),
    contactY: bl.y,
    halfX: Math.max(cell, ((bl.x1 - bl.x0 + 1) / NX) * size.x * 0.60),
  }));

  // One width for all six. A contact patch is only as wide as the part of the
  // tyre actually touching down, and the middle wheels report a narrower patch
  // than the corners — but they are the same wheel, so the widest honest
  // measurement is the right one to trust for every station.
  const widths = st.map((t) => t.halfX).sort((p, q) => p - q);
  const wStar = widths[widths.length >> 1]!;
  for (const t of st) t.halfX = Math.max(t.halfX, wStar);

  // Station order must match the rover's own: left fore, mid, aft, then right.
  const mid = (box.min.x + box.max.x) / 2;
  const left = st.filter((s) => s.x < mid).sort((p, q) => q.z - p.z);
  const right = st.filter((s) => s.x >= mid).sort((p, q) => q.z - p.z);
  if (left.length !== 3 || right.length !== 3) return [];
  return [...left, ...right];
}

/**
 * The tyre radius, from the wheel's own lower arc.
 *
 * Once the contact patch is known this needs no fitting at all, because a
 * circle resting on a point puts its centre straight above that point. Take
 * any other point on the tyre's lower arc, at offset u along the vehicle and
 * height v above the contact, and it must satisfy
 *
 *     u² + (v − r)² = r²   ⟹   r = (u² + v²) / 2v
 *
 * so EVERY point of the arc independently measures the radius. The answer is
 * the median of those measurements, which is why a bracket in the sample cannot
 * move it: outliers have to outnumber the tyre to win, and they never do.
 *
 * This is the third method tried here. Fitting the outer points fitted the
 * contamination; fitting the whole envelope algebraically collapsed onto
 * grousers. A median of per-point estimates is robust in a way least squares
 * simply is not.
 */
function tyreRadius(us: number[], vs: number[]): number {
  const est: number[] = [];
  let vMax = 0;
  for (const v of vs) if (v > vMax) vMax = v;
  if (!(vMax > 0)) return 0;
  for (let i = 0; i < us.length; i++) {
    const v = vs[i]!;
    if (v < vMax * 0.12) continue;   // near the contact the estimate is 0/0
    const u = us[i]!;
    est.push((u * u + v * v) / (2 * v));
  }
  if (est.length < 8) return 0;
  est.sort((p, q) => p - q);
  return est[est.length >> 1]!;
}

/**
 * Cut the six wheels out of an imported mesh so they can be driven.
 *
 * The asset is split by MATERIAL, not by part — all ten of its meshes span the
 * whole vehicle — so there is no wheel node to rig. There are, however, wheels:
 * sampling the lower third of the model gives three clean clusters along its
 * long axis and an even split across its lateral one, which is a rocker-bogie
 * seen from below.
 *
 * So the wheels are extracted geometrically. Every triangle whose centroid
 * falls inside one of six boxes is moved into a wheel of its own, re-centred on
 * a FITTED axle (see fitAxle) so that rotating about local X is rolling.
 * What is left is the chassis. Nothing is deleted and no vertex moves relative
 * to its neighbours — the model is only regrouped.
 *
 * It runs in two passes for one reason: a wheel is spread across several
 * materials, and the pivot has to be fitted to ALL of its geometry before any
 * of it is re-centred. Choosing the pivot from the first material to arrive is
 * how the wheels ended up orbiting a point that was not their axle.
 *
 * This is the difference between borrowing a picture of a rover and borrowing a
 * rover.
 */
function extractWheels(model: THREE.Object3D): THREE.Group[] {
  // Everything below happens in the MODEL's own local space.
  //
  // The first attempt measured the bounding box with setFromObject, which is
  // WORLD space — and by that point the model had already been scaled to metres.
  // So triangle centroids in the hundreds were being tested against bounds in
  // single digits and nothing ever matched: the extraction silently claimed
  // zero triangles and the rover rode rigid exactly as before.
  model.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(model.matrixWorld).invert();

  const meshes: THREE.Mesh[] = [];
  model.traverse((o) => { if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh); });

  const rels = new Map<THREE.Mesh, THREE.Matrix4>();
  const box = new THREE.Box3();
  for (const m of meshes) {
    const rel = new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld);
    rels.set(m, rel);
    const g = m.geometry;
    if (!g.boundingBox) g.computeBoundingBox();
    box.union(g.boundingBox!.clone().applyMatrix4(rel));
  }
  if (box.isEmpty()) return [];

  const size = new THREE.Vector3();
  const centre = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(centre);

  // Where the wheels actually are, measured off the mesh (see findStations).
  const stations = findStations(meshes, rels, box, size);
  if (stations.length !== 6) {
    // Deliberately loud. A silent return here is what let a no-op extraction
    // ship twice: the rover simply rode rigid and nothing said why.
    console.warn(`[rover] wheel rigging skipped — found ${stations.length} ground `
      + 'contacts, expected 6. The imported model rides rigid.');
    return [];
  }

  // A wheel may reach no further along the vehicle than halfway to its
  // neighbour; past that the sample would be picking up the next wheel.
  //
  // The gap is measured down ONE SIDE only. Comparing all six against each
  // other pairs the left and right wheels of the same station — which sit at
  // the same place along the vehicle — and yields a spacing of nearly zero.
  let nearest = Infinity;
  for (const side of [stations.slice(0, 3), stations.slice(3)]) {
    for (let i = 0; i + 1 < side.length; i++) {
      const d = Math.abs(side[i]!.z - side[i + 1]!.z);
      if (d < nearest) nearest = d;
    }
  }
  const halfZ = isFinite(nearest) && nearest > 0 ? nearest * 0.5 : size.z * 0.12;

  const groups = stations.map(() => new THREE.Group());

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();

  // ── pass 1: classify every triangle, and gather each wheel's point cloud ───
  interface Job { mesh: THREE.Mesh; rel: THREE.Matrix4; buckets: Map<number, number[]> }
  const jobs: Job[] = [];
  const cloud = stations.map(() => ({ xs: [] as number[], ys: [] as number[], zs: [] as number[] }));

  for (const mesh of meshes) {
    const rel = rels.get(mesh)!;
    const geo = mesh.geometry;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const idx = geo.getIndex();
    const triCount = idx ? idx.count / 3 : pos.count / 3;
    const at = (t: number, k: number): number => (idx ? idx.getX(t * 3 + k) : t * 3 + k);

    const buckets = new Map<number, number[]>();
    for (let t = 0; t < triCount; t++) {
      const i0 = at(t, 0);
      const i1 = at(t, 1);
      const i2 = at(t, 2);
      a.fromBufferAttribute(pos, i0).applyMatrix4(rel);
      b.fromBufferAttribute(pos, i1).applyMatrix4(rel);
      c.fromBufferAttribute(pos, i2).applyMatrix4(rel);
      const cx = (a.x + b.x + c.x) / 3;
      const cy = (a.y + b.y + c.y) / 3;
      const cz = (a.z + b.z + c.z) / 3;
      // A generous first cut around each station — the wheel is separated from
      // whatever else lands here by the disc test in pass 2, once its radius is
      // known. Nothing above roughly a wheel's height can be tyre.
      let who = -1;
      for (let s = 0; s < stations.length; s++) {
        const st = stations[s]!;
        if (Math.abs(cx - st.x) < st.halfX
            && Math.abs(cz - st.z) < halfZ
            && cy < st.contactY + halfZ * 2) {
          who = s;
          break;
        }
      }
      let arr = buckets.get(who);
      if (!arr) { arr = []; buckets.set(who, arr); }
      arr.push(i0, i1, i2);
    }
    if (buckets.size <= 1) continue;   // none of this material is a wheel

    for (const [who, indices] of buckets) {
      if (who < 0) continue;
      const seen = new Set<number>();
      const cl = cloud[who]!;
      for (const i of indices) {
        if (seen.has(i)) continue;
        seen.add(i);
        a.fromBufferAttribute(pos, i).applyMatrix4(rel);
        cl.xs.push(a.x); cl.ys.push(a.y); cl.zs.push(a.z);
      }
    }
    jobs.push({ mesh, rel, buckets });
  }

  // ── the axles ─────────────────────────────────────────────────────────────
  // Each wheel's radius is measured off its own lower arc, then reconciled
  // across all six, because the six wheels of a rover are the same wheel six
  // times. One collapsed measurement is outvoted rather than believed.
  //
  // The axle itself needs no fit: it is the contact patch, straight up by the
  // radius. A circle resting on the ground puts its centre directly above the
  // point it rests on.
  const each = stations.map((st, i) => {
    const cl = cloud[i]!;
    if (cl.ys.length < 60) return 0;
    // The lower arc, binned along the vehicle so one low vertex per bin is all
    // that is sampled — nothing on a rover lies beneath its own tyre.
    const BINS = 48;
    const lowY = new Array<number>(BINS).fill(Infinity);
    const lowZ = new Array<number>(BINS).fill(0);
    for (let k = 0; k < cl.ys.length; k++) {
      const u = (cl.zs[k]! - st.z) / halfZ;               // −1 … 1 across the station
      if (u < -1 || u > 1) continue;
      const bi = Math.min(BINS - 1, Math.max(0, Math.floor(((u + 1) / 2) * BINS)));
      if (cl.ys[k]! < lowY[bi]!) { lowY[bi] = cl.ys[k]!; lowZ[bi] = cl.zs[k]!; }
    }
    const us: number[] = [];
    const vs: number[] = [];
    for (let bi = 0; bi < BINS; bi++) {
      if (!isFinite(lowY[bi]!)) continue;
      us.push(lowZ[bi]! - st.z);
      vs.push(lowY[bi]! - st.contactY);
    }
    return tyreRadius(us, vs);
  });

  const good = each.filter((r) => r > 0).sort((p, q) => p - q);
  const rStar = good.length >= 3 ? good[good.length >> 1]! : 0;
  if (!(rStar > 0)) {
    console.warn('[rover] wheel rigging skipped — could not measure a tyre '
      + 'radius from the lower arcs. The imported model rides rigid.');
    return [];
  }

  const pivots = stations.map((st) => (rStar > 0
    ? { at: new THREE.Vector3(st.x, st.contactY + rStar, st.z), r: rStar }
    : null));

  // ── pass 2: rebuild each wheel about its axle ─────────────────────────────
  for (const { mesh, rel, buckets } of jobs) {
    const nrm = new THREE.Matrix3().getNormalMatrix(rel);
    const geo = mesh.geometry;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const nor = geo.getAttribute('normal') as THREE.BufferAttribute | undefined;
    const uv = geo.getAttribute('uv') as THREE.BufferAttribute | undefined;

    const reject: number[] = [];
    for (const [who, raw] of buckets) {
      let indices = raw;
      if (who < 0) continue;
      const found = pivots[who];
      if (!found) { reject.push(...indices); continue; }
      const pivot = found.at;

      // ── the wheel is the disc, and only the disc ─────────────────────────
      // Fitting a good axle is not enough on its own. A slot wide enough to
      // catch the whole tyre also catches the bracket behind it, and a bracket
      // welded into the wheel group swings around the axle exactly as wrongly
      // as a bad pivot would. So every triangle is re-tested against the
      // FITTED radius, and whatever falls outside goes back to the chassis
      // where it belongs. Nothing is lost — it is only re-sorted.
      const keep: number[] = [];
      for (let t = 0; t < indices.length; t += 3) {
        a.fromBufferAttribute(pos, indices[t]!).applyMatrix4(rel);
        b.fromBufferAttribute(pos, indices[t + 1]!).applyMatrix4(rel);
        c.fromBufferAttribute(pos, indices[t + 2]!).applyMatrix4(rel);
        const my = (a.y + b.y + c.y) / 3 - pivot.y;
        const mz = (a.z + b.z + c.z) / 3 - pivot.z;
        if (Math.hypot(my, mz) <= found.r * 1.10) {
          keep.push(indices[t]!, indices[t + 1]!, indices[t + 2]!);
        } else {
          reject.push(indices[t]!, indices[t + 1]!, indices[t + 2]!);
        }
      }
      if (keep.length < 3) continue;
      indices = keep;
      // Compact: copy only the vertices this wheel actually references, baked
      // into model space, so nothing carries the parent's transform around.
      const remap = new Map<number, number>();
      const px: number[] = [];
      const nx: number[] = [];
      const tx: number[] = [];
      const out: number[] = [];
      for (const i of indices) {
        let j = remap.get(i);
        if (j === undefined) {
          j = px.length / 3;
          remap.set(i, j);
          a.fromBufferAttribute(pos, i).applyMatrix4(rel).sub(pivot);
          px.push(a.x, a.y, a.z);
          if (nor) {
            b.fromBufferAttribute(nor, i).applyMatrix3(nrm).normalize();
            nx.push(b.x, b.y, b.z);
          }
          if (uv) tx.push(uv.getX(i), uv.getY(i));
        }
        out.push(j);
      }
      const sub = new THREE.BufferGeometry();
      sub.setAttribute('position', new THREE.Float32BufferAttribute(px, 3));
      if (nx.length) sub.setAttribute('normal', new THREE.Float32BufferAttribute(nx, 3));
      if (tx.length) sub.setAttribute('uv', new THREE.Float32BufferAttribute(tx, 2));
      sub.setIndex(out);
      const g = groups[who]!;
      g.position.copy(pivot);
      // Every piece of this wheel is already expressed about the shared axle,
      // so no piece needs an offset of its own.
      g.add(new THREE.Mesh(sub, mesh.material));
    }
    const rest = (buckets.get(-1) ?? []).concat(reject);
    if (rest.length) geo.setIndex(rest);
    else mesh.visible = false;
  }

  for (const g of groups) model.add(g);
  return groups;
}

/**
 * Textured variant, for the imported model.
 *
 * The Sketchfab asset declares KHR_materials_pbrSpecularGlossiness as a REQUIRED
 * extension, and three removed support for it. So the loader binds no base
 * colour at all and every surface renders as a silhouette — which is the black
 * rover, and it is not a lighting bug however much it looks like one.
 *
 * Rebuilding the materials here fixes that and buys something better besides:
 * the model ends up lit by the PLOT's sun, the same uniform the terrain and the
 * rocks read, instead of by the fixed scene light meant for the outer tiers. A
 * borrowed mesh that shades against a different sun than the ground under it
 * would look wrong in a way nobody could name.
 */
function texturedMaterial(uniforms: Record<string, THREE.IUniform>,
                          map: THREE.Texture | null): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uSunDir: uniforms['uSunDir']!,
      uSunFlux: uniforms['uSunFlux']!,
      uMap: { value: map },
      uHasMap: { value: map ? 1 : 0 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vN;
      varying vec2 vUv;
      #include <logdepthbuf_pars_vertex>
      bool isPerspectiveMatrix(mat4 m) { return m[2][3] == -1.0; }
      void main(){
        vN = normalize(mat3(modelMatrix) * normal);
        vUv = uv;
        gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform vec3 uSunDir;
      uniform float uSunFlux, uHasMap;
      uniform sampler2D uMap;
      varying vec3 vN;
      varying vec2 vUv;
      #include <logdepthbuf_pars_fragment>
      void main(){
        #include <logdepthbuf_fragment>
        vec3 N = normalize(vN);
        vec3 base = uHasMap > 0.5 ? texture2D(uMap, vUv).rgb : vec3(0.55);
        float ci = max(dot(N, uSunDir), 0.0);
        vec3 col = base * ci * vec3(1.32, 1.02, 0.78) * uSunFlux;
        col += base * vec3(0.34, 0.26, 0.20) * (0.40 + 0.60 * max(N.y, 0.0));
        vec3 H = normalize(uSunDir + vec3(0.0, 1.0, 0.0));
        col += vec3(1.0, 0.92, 0.78) * pow(max(dot(N, H), 0.0), 24.0) * 0.22 * uSunFlux;
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    side: THREE.DoubleSide,
  });
}

/** Faceted metal, lit by the plot's own sun like everything else out here. */
function bodyMaterial(uniforms: Record<string, THREE.IUniform>,
                      colour: [number, number, number],
                      metal: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uSunDir: uniforms['uSunDir']!,
      uSunFlux: uniforms['uSunFlux']!,
      uCol: { value: new THREE.Vector3(...colour) },
      uMetal: { value: metal },
    },
    vertexShader: /* glsl */ `
      varying vec3 vN;
      varying vec3 vP;
      #include <logdepthbuf_pars_vertex>
      bool isPerspectiveMatrix(mat4 m) { return m[2][3] == -1.0; }
      void main(){
        vN = normalize(mat3(modelMatrix) * normal);
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vP = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform vec3 uSunDir, uCol;
      uniform float uSunFlux, uMetal;
      varying vec3 vN;
      varying vec3 vP;
      #include <logdepthbuf_pars_fragment>
      void main(){
        #include <logdepthbuf_fragment>
        vec3 N = normalize(vN);
        float ci = max(dot(N, uSunDir), 0.0);
        vec3 col = uCol * ci * vec3(1.32, 1.02, 0.78) * uSunFlux;
        // Butterscotch sky fill, same dome the terrain sits under. Machined
        // surfaces take far more of it than regolith does, which is most of why
        // hardware reads as hardware next to rock.
        col += uCol * vec3(0.34, 0.26, 0.20) * (0.40 + 0.60 * max(N.y, 0.0)) * (0.5 + uMetal);
        // A cheap specular lobe so the deck catches the sun as it turns.
        vec3 H = normalize(uSunDir + vec3(0.0, 1.0, 0.0));
        col += vec3(1.0, 0.92, 0.78) * pow(max(dot(N, H), 0.0), 26.0) * uMetal * 0.5 * uSunFlux;
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Survey tracking — the rover's memory of what it has driven over.
// ─────────────────────────────────────────────────────────────────────────────

const SURVEY_KEY = 'atlas.surveyed.v1';

function loadSurveyed(): Set<string> {
  try {
    const raw = localStorage.getItem(SURVEY_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveSurveyed(s: Set<string>): void {
  try {
    localStorage.setItem(SURVEY_KEY, JSON.stringify([...s]));
  } catch { /* private mode */ }
}

/** Persistent set of surveyed cell IDs. Shared across all rover instances. */
const surveyedCells = loadSurveyed();

/** Check whether a cell has been surveyed. */
export function isSurveyed(cellId: string): boolean {
  return surveyedCells.has(cellId);
}

type SurveyListener = (cellId: string) => void;
const surveyListeners = new Set<SurveyListener>();

/**
 * Told when a sub-plot is newly surveyed, so the caller can file the parcel
 * with the server. The rover holds no network seam of its own, and mapping a
 * sub-plot to its res-8 parcel needs the loaded plot, which only the caller
 * has. `applySurveyedFor` below is the server's own answer coming back and
 * deliberately does not fire this.
 */
export function onSurveyed(fn: SurveyListener): () => void {
  surveyListeners.add(fn);
  return () => { surveyListeners.delete(fn); };
}

/** Mark a cell as surveyed and persist. Returns true if it was newly surveyed. */
export function markSurveyed(cellId: string): boolean {
  if (surveyedCells.has(cellId)) return false;
  surveyedCells.add(cellId);
  saveSurveyed(surveyedCells);
  for (const fn of surveyListeners) fn(cellId);
  return true;
}

/** How many cells the rover has surveyed in total. */
export function surveyedCount(): number {
  return surveyedCells.size;
}

/**
 * Makes the record for the sub-plots in hand agree with `surveyed`.
 *
 * The server keys surveys by res-8 cell (`server/engine/claims.ts`) and this
 * set is keyed by sub-plot id, so only the caller holding a loaded plot can
 * map one to the other. Ids outside `known` belong to other sites and are
 * left alone. Returns true when anything moved.
 */
export function applySurveyedFor(
  known: readonly string[], surveyed: ReadonlySet<string>,
): boolean {
  let changed = false;
  for (const id of known) {
    const want = surveyed.has(id);
    if (want === surveyedCells.has(id)) continue;
    if (want) surveyedCells.add(id);
    else surveyedCells.delete(id);
    changed = true;
  }
  if (changed) saveSurveyed(surveyedCells);
  return changed;
}

/** Clear all survey data (for testing). */
export function clearSurveyData(): void {
  surveyedCells.clear();
  saveSurveyed(surveyedCells);
}

/** A survey job: the rover drives to a target cell or surveys an entire plot. */
export interface SurveyJob {
  /** Target cell centre in region-local metres, or null for "survey plot". */
  target: { x: number; z: number } | null;
  /** Cell id for single-cell jobs. */
  cellId?: string;
}

export class Rover {
  readonly group = new THREE.Group();
  readonly state: RoverState = { x: 0, z: 0, yaw: 0, speed: 0, slipping: false, slopeDeg: 0 };

  private field: PlotField;
  private exag: number;
  /** Where the plot mesh draws the ground - see groundAt(). */
  private meshHeightAt: ((x: number, z: number) => number) | null;
  private minY: number;
  /** Kept so the imported model can share the plot's sun. */
  private uniformsRef: Record<string, THREE.IUniform>;
  private body = new THREE.Group();
  /** Everything hand-built, so the imported model can replace it in one line. */
  private rig = new THREE.Group();
  private model: THREE.Group | null = null;
  /** The imported model's wheels, cut out so they can be driven. */
  private modelWheels: THREE.Group[] = [];
  /** Rest positions of those wheels in the model's own frame. */
  private modelWheelRest: THREE.Vector3[] = [];
  /**
   * Where the IMPORTED model's wheels actually are, in body metres.
   *
   * The drawn vehicle has to be the simulated one. The imported model is three
   * times life size, and its wheelbase is a different fraction of its length
   * than ours — its axles sit 2.7 m fore and aft where ours sit 1.25 — so
   * sampling the ground at our stations put the terrain reading nowhere near
   * the wheels being drawn. On a slope they clipped by the better part of a
   * metre. These come from the same contact patches the rigging was cut from.
   */
  private modelStations: Array<[number, number]> = [];
  private modelWheelRadius = 0;
  skin: 'built' | 'curiosity' = 'built';
  private wheels: THREE.Group[] = [];
  private rockers: THREE.Mesh[] = [];
  private mats: THREE.ShaderMaterial[] = [];
  private geos: THREE.BufferGeometry[] = [];
  /** Body-local wheel stations: [x, z]. */
  private stations: Array<[number, number]> = [];
  private spin = 0;
  /** [x, z, r, baseY, height] per rock, from the scatter. See RockField. */
  private obstacles: Float32Array = new Float32Array(0);
  /** The scatter, for the boulders that are a step rather than a bump. */
  private rocks: RockField | null = null;
  /** Metres travelled, for the telemetry. */
  odometerM = 0;
  /** True while a wheel is riding a rock rather than the ground. */
  onRock = false;
  /** Auto-drive state. */
  private autoTimer = 0;
  private autoHeading = 0;

  // ── Survey machinery ───────────────────────────────────────────────────
  /** Queue of survey jobs the rover is working through. */
  private surveyQueue: SurveyJob[] = [];
  /** The sub-plots this rover knows about, for cell-crossing detection. */
  private subPlots: SubPlot[] = [];
  /** Last cell the rover was standing in, for crossing detection. */
  private lastCellId: string | null = null;
  /** Callback fired when a cell is newly surveyed. */
  onSurvey: ((cellId: string, cell: SubPlot) => void) | null = null;

  constructor(field: PlotField, uniforms: Record<string, THREE.IUniform>, exag: number,
              meshHeightAt: ((x: number, z: number) => number) | null = null) {
    this.field = field;
    this.exag = exag;
    this.meshHeightAt = meshHeightAt;
    this.minY = field.meta.elevation.min_m;
    this.body.add(this.rig);
    this.uniformsRef = uniforms;

    const alu = bodyMaterial(uniforms, [0.62, 0.60, 0.56], 0.85);
    const gold = bodyMaterial(uniforms, [0.86, 0.66, 0.30], 0.95);
    const dark = bodyMaterial(uniforms, [0.13, 0.13, 0.145], 0.35);
    const tyre = bodyMaterial(uniforms, [0.16, 0.155, 0.15], 0.15);
    this.mats.push(alu, gold, dark, tyre);

    const keep = <T extends THREE.BufferGeometry>(g: T): T => { this.geos.push(g); return g; };

    // ── warm electronics box ────────────────────────────────────────────────
    // The gold is not decoration: the real thing is wrapped in multi-layer
    // insulation because it has to survive a night at -90C, and that wrapping
    // is the most recognisable thing about it.
    const hull = new THREE.Mesh(
      keep(new THREE.BoxGeometry(ROVER.widthM * 0.62, 0.55, ROVER.lengthM * 0.72)), gold);
    hull.position.y = ROVER.deckM;
    this.rig.add(hull);

    // Radiator deck on top, angled to shed dust.
    const deck = new THREE.Mesh(
      keep(new THREE.BoxGeometry(ROVER.widthM * 0.66, 0.06, ROVER.lengthM * 0.60)), alu);
    deck.position.set(0, ROVER.deckM + 0.31, 0.06);
    deck.rotation.x = -0.05;
    this.rig.add(deck);

    // ── RTG, aft ────────────────────────────────────────────────────────────
    // The thing that keeps it alive through the night, and the reason a Mars
    // rover does not care how long the dust storm lasts.
    const rtg = new THREE.Mesh(
      keep(new THREE.CylinderGeometry(0.26, 0.26, 0.62, 12)), dark);
    rtg.rotation.x = Math.PI / 2;
    rtg.position.set(0, ROVER.deckM + 0.28, -ROVER.lengthM * 0.48);
    this.rig.add(rtg);
    for (let i = 0; i < 8; i++) {
      const fin = new THREE.Mesh(keep(new THREE.BoxGeometry(0.02, 0.34, 0.60)), alu);
      fin.position.copy(rtg.position);
      fin.rotation.z = (i / 8) * Math.PI * 2;
      this.rig.add(fin);
    }

    // ── mast and camera head ────────────────────────────────────────────────
    const mast = new THREE.Mesh(
      keep(new THREE.CylinderGeometry(0.045, 0.055, ROVER.mastM - ROVER.deckM, 8)), alu);
    mast.position.set(-0.34, (ROVER.deckM + ROVER.mastM) / 2 + 0.12, ROVER.lengthM * 0.26);
    this.rig.add(mast);
    const head = new THREE.Mesh(keep(new THREE.BoxGeometry(0.44, 0.16, 0.14)), dark);
    head.position.set(-0.34, ROVER.mastM + 0.14, ROVER.lengthM * 0.26);
    this.rig.add(head);
    for (const dx of [-0.13, 0.13]) {
      const eye = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.035, 0.035, 0.06, 10)), gold);
      eye.rotation.x = Math.PI / 2;
      eye.position.set(-0.34 + dx, ROVER.mastM + 0.14, ROVER.lengthM * 0.26 + 0.09);
      this.rig.add(eye);
    }

    // High-gain antenna, because something has to talk to the orbiter.
    const dish = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.30, 0.30, 0.03, 14)), alu);
    dish.position.set(0.42, ROVER.deckM + 0.52, -ROVER.lengthM * 0.20);
    dish.rotation.set(0.5, 0, 0.3);
    this.rig.add(dish);

    this.group.add(this.body);

    // ── rocker-bogie ────────────────────────────────────────────────────────
    // Six wheels on two rockers, a bogie each. It is what keeps all six down on
    // ground like this, and it is the silhouette that says Mars before any
    // texture does.
    const wheelGeo = keep(new THREE.CylinderGeometry(
      ROVER.wheelRadiusM, ROVER.wheelRadiusM, ROVER.wheelWidthM, 16));
    // A cleat runs ACROSS the tread, along the axle. These had the long
    // dimension tangential instead, which made each one a 0.37 m slab lying
    // around the rim with its corners standing 72 mm proud of the tyre — a
    // third of the wheel's radius. The physics puts the TYRE on the ground, so
    // every wheel drove with its grouser tips 7 cm into the terrain, on every
    // surface including dead flat. Curiosity's real grousers stand about 7 mm
    // off a 250 mm wheel; these now stand 18.
    const grouserGeo = keep(new THREE.BoxGeometry(
      ROVER.wheelWidthM * 0.92, 0.03, ROVER.wheelRadiusM * 0.30));

    for (const side of [-1, 1]) {
      for (const az of ROVER.axleZ) {
        const g = new THREE.Group();
        const w = new THREE.Mesh(wheelGeo, tyre);
        w.rotation.z = Math.PI / 2;
        g.add(w);
        // Grousers: the chevron cleats that are the only reason a wheel finds
        // purchase in loose regolith at all.
        for (let i = 0; i < 12; i++) {
          const cl = new THREE.Mesh(grouserGeo, alu);
          const a = (i / 12) * Math.PI * 2;
          cl.position.set(0, Math.cos(a) * ROVER.wheelRadiusM, Math.sin(a) * ROVER.wheelRadiusM);
          cl.rotation.x = -a;
          g.add(cl);
        }
        this.rig.add(g);
        this.wheels.push(g);
        this.stations.push([side * ROVER.halfTrack, az]);

        // The arm from the body down to this wheel. Rescaled per frame to
        // whatever the suspension is actually doing.
        const arm = new THREE.Mesh(keep(new THREE.BoxGeometry(0.07, 1, 0.07)), alu);
        this.rig.add(arm);
        this.rockers.push(arm);
      }
    }
  }

  /** The stations of whichever vehicle is being drawn. */
  private activeStations(): Array<[number, number]> {
    return this.skin === 'curiosity' && this.modelStations.length === 6
      ? this.modelStations : this.stations;
  }

  /** Tyre radius of whichever vehicle is being drawn. */
  private activeRadius(): number {
    return this.skin === 'curiosity' && this.modelWheelRadius > 0
      ? this.modelWheelRadius : ROVER.wheelRadiusM;
  }

  /**
   * Ground height in the SAME space the shader draws, at plot-local x,z.
   *
   * `DtmPlot.meshHeightAt`, which is the drawn mesh itself: the lattice's own
   * triangle under the contact patch, displaced by the mip `uLod` selects.
   * This was a 9-tap MAX over one lattice step of `heightAt`, because the mesh
   * drew a ridge crest that level 0 of the height field sat below and a mean
   * sank the rover until the ground came through between body and wheels. The
   * max cost a few centimetres of float in a hollow and, at 17.65 m of lattice
   * times 2.5 relief, metres of it on a slope. There is nothing left to
   * approximate: the sampler draws the crest.
   *
   * Absent a plot mesh - the dojo builds its terrain from `heightAt` directly -
   * the field's own level 0 is the drawn ground.
   */
  private groundAt(x: number, z: number): number {
    if (this.meshHeightAt) return this.meshHeightAt(x, z);
    return (this.field.heightAt(x, z) - this.minY) * this.exag;
  }

  /**
   * Hand the rover the rocks it is about to drive into.
   *
   * Before this the wheels read only the height field, so a boulder was a thing
   * the vehicle passed through. Six wheels scanning a few thousand rocks is
   * cheap enough to do every frame, and it converts the scatter from decoration
   * into terrain.
   */
  setObstacles(o: Float32Array): void {
    this.obstacles = o;
  }

  /**
   * The scatter itself, for the boulders the wheels cannot climb.
   *
   * `setObstacles` gives the vehicle rocks to ride OVER; this gives it the
   * field's own lookup for the ones it has to go round. Null before a scatter
   * exists, and on a body that draws none.
   */
  setRocks(rocks: RockField | null): void {
    this.rocks = rocks;
  }

  /**
   * Highest rock top under a wheel, or -Infinity.
   *
   * A wheel rides the rock it is standing on, so this is a max over everything
   * whose footprint it overlaps — the same argument as sampling the ground by
   * maximum rather than by mean.
   */
  private rockTopAt(x: number, z: number): number {
    const o = this.obstacles;
    if (o.length === 0) return -Infinity;
    const reach = ROVER.wheelRadiusM;
    let top = -Infinity;
    for (let i = 0; i < o.length; i += OBSTACLE_STRIDE) {
      const dx = o[i]! - x;
      const dz = o[i + 1]! - z;
      const rr = o[i + 2]! + reach;
      if (dx * dx + dz * dz > rr * rr) continue;
      const t = o[i + 3]! * this.exag + o[i + 4]!;
      if (t > top) top = t;
    }
    return top;
  }

  setExaggeration(x: number): void {
    this.exag = x;
  }

  /** Drop the rover onto the ground at a point, facing a heading. */
  placeAt(x: number, z: number, yaw = 0): void {
    this.state.x = x;
    this.state.z = z;
    this.state.yaw = yaw;
    this.state.speed = 0;
    this.update(0, { throttle: 0, steer: 0, brake: true });
  }

  update(dt: number, c: RoverControls): void {
    const s = this.state;
    const step = Math.min(dt, 0.05);

    // ── slope resistance ────────────────────────────────────────────────────
    // Grade is what a rover actually fights. Sampling it ALONG the heading
    // rather than as a magnitude means climbing costs and descending does not,
    // which is the asymmetry that makes terrain feel like terrain.
    const ahead = 1.5;
    const hx = Math.sin(s.yaw);
    const hz = Math.cos(s.yaw);
    const h0 = this.groundAt(s.x, s.z);
    const h1 = this.groundAt(s.x + hx * ahead, s.z + hz * ahead);
    const grade = (h1 - h0) / (ahead * this.exag);
    const gradeDeg = (Math.atan(grade) * 180) / Math.PI;
    s.slopeDeg = this.field.slopeAt(s.x, s.z);

    // Above the limit the wheels simply do not hold.
    s.slipping = s.slopeDeg > ROVER.slopeLimitDeg;
    const climbPenalty = THREE.MathUtils.clamp(1 - Math.max(0, gradeDeg) / ROVER.slopeLimitDeg, 0.12, 1);
    const target = c.brake ? 0 : c.throttle * ROVER.maxSpeed * (s.slipping ? 0.25 : climbPenalty);

    // First-order approach to the target: a rover has torque and mass, and the
    // half second it takes to get moving is part of how it feels.
    const k = c.brake ? 4.5 : 1.8;
    s.speed += (target - s.speed) * (1 - Math.exp(-k * step));
    if (Math.abs(s.speed) < 0.004) s.speed = 0;

    // Steering authority scales with speed — a skid-steer rover pivots when it
    // is moving and not at all when it is stopped.
    s.yaw += c.steer * ROVER.turnRate * step * THREE.MathUtils.clamp(Math.abs(s.speed) / 1.2, 0, 1)
      * Math.sign(s.speed || 1);

    s.x += Math.sin(s.yaw) * s.speed * step;
    s.z += Math.cos(s.yaw) * s.speed * step;
    this.spin += (s.speed * step) / ROVER.wheelRadiusM;
    if (ROVER_PROBE) (globalThis as unknown as Record<string, unknown>)['__rover'] = this;
    this.odometerM += Math.abs(s.speed) * step;

    // Keep it on its own ground.
    const R = this.field.meta.hex.radius_m;
    let inside = false;
    for (const p of this.field.placements) {
      if (this.field.insideHex(s.x - p.x_m, s.z - p.z_m)) { inside = true; break; }
    }
    if (!inside) {
      // Back it off rather than stopping dead — a hard stop at an invisible
      // wall reads as a bug, a refusal to continue reads as an edge.
      s.x -= Math.sin(s.yaw) * s.speed * step;
      s.z -= Math.cos(s.yaw) * s.speed * step;
      s.speed *= -0.25;
    }
    void R;

    // boulders
    // A rock a wheel can ride is terrain and `rockTopAt` above puts the wheel
    // on top of it. One standing proud of a wheel RADIUS is a step, and
    // driving at those used to teleport the vehicle onto the roof of a
    // two-metre block. Slide along it rather than stop dead, and bleed speed
    // with the depth, so a glancing hit steers off it and a square one noses
    // up against it and stops.
    if (this.rocks?.pushOut(s.x, s.z, this.groundAt(s.x, s.z) + ROVER.wheelRadiusM,
                            ROVER.widthM * 0.5, _push)) {
      s.x = _push.x;
      s.z = _push.z;
      s.speed *= Math.max(0.4, 1 - _push.depth * 2);
    }

    // ── put the wheels on the ground ────────────────────────────────────────
    const st = this.activeStations();
    const wr = this.activeRadius();
    const cy = Math.cos(s.yaw);
    const sy = Math.sin(s.yaw);
    let sumY = 0;
    let anyRock = false;
    const contacts: THREE.Vector3[] = [];
    for (let i = 0; i < st.length; i++) {
      const [lx, lz] = st[i]!;
      const wx = s.x + lx * cy + lz * sy;
      const wz = s.z - lx * sy + lz * cy;
      let wy = this.groundAt(wx, wz) + wr;
      // Ride the rock if there is one taller than the ground beneath it.
      const rock = this.rockTopAt(wx, wz);
      if (rock > wy - wr) {
        wy = rock + wr;
        anyRock = true;
      }
      contacts.push(_v.clone().set(wx, wy, wz));
      sumY += wy;
    }

    // Chassis takes the attitude of the plane the six wheels describe. Fitting
    // it from the fore/aft and left/right contact pairs is enough: a rover's
    // body is rigid, so pitch and roll are the only freedoms it has.
    const meanY = sumY / contacts.length;
    const fore = (contacts[0]!.y + contacts[3]!.y) / 2;
    const aft = (contacts[2]!.y + contacts[5]!.y) / 2;
    const left = (contacts[0]!.y + contacts[1]!.y + contacts[2]!.y) / 3;
    const right = (contacts[3]!.y + contacts[4]!.y + contacts[5]!.y) / 3;
    const pitch = Math.atan2(fore - aft, st[0]![1] - st[2]![1]);
    const roll = Math.atan2(right - left, Math.abs(st[3]![0] - st[0]![0]));

    // ── keep the belly off the crest ────────────────────────────────────────
    // The body sits at the MEAN of its contacts, which is right on a plane and
    // wrong on a dome: crossing a crest, the ground between the wheels rises
    // above the plane the wheels describe and the chassis submarines into it.
    // A real rover high-centres there; this one just intersected the hill.
    //
    // The lift is measured against the TILTED plane, not against a level datum.
    // Measuring it against the world's idea of up is what a first attempt did,
    // and on a 34 degree hillside the uphill corner of the footprint stands a
    // metre above the body's centre for reasons that are entirely accounted for
    // by the tilt already — so it lifted the whole vehicle a metre, ran the
    // suspension out of travel, and left the wheels hanging in the air. On
    // planar ground of any steepness this term is now zero.
    //
    // Lifting costs nothing while there is travel left, because the wheels are
    // placed from the ground under themselves further down: raising the body
    // extends the suspension rather than picking the wheels up. So the lift is
    // capped at what the suspension can actually absorb.
    const gradZ = (fore - aft) / (st[0]![1] - st[2]![1]);
    const gradX = (right - left) / (st[3]![0] - st[0]![0]);
    const baseY = meanY - wr + 0.02;
    let lift = 0;
    for (let a = -1; a <= 1; a++) {
      for (let b = -1; b <= 1; b++) {
        const lx = a * st[0]![0];
        const lz = b * st[0]![1];
        const g = this.groundAt(s.x + lx * cy + lz * sy, s.z - lx * sy + lz * cy);
        const planeY = baseY + lz * gradZ + lx * gradX;
        const need = g + 0.05 - planeY;
        if (need > lift) lift = need;
      }
    }
    const bodyY = baseY + Math.min(lift, wr * 1.4);

    this.onRock = anyRock;
    this.body.position.set(s.x, bodyY, s.z);
    this.body.rotation.set(0, 0, 0);
    // POSITIVE yaw.
    //
    // The heading advances along (sin yaw, cos yaw), and rotateY(t) carries the
    // model's +Z to (sin t, cos t) — so the angle is yaw, not its negation.
    // Negated, the whole vehicle faced the mirror of its own heading, which is
    // subtle enough to miss while driving straight and is precisely why the
    // wheels looked like they were rolling backwards.
    this.body.rotateY(s.yaw);
    this.body.rotateX(-pitch);
    // POSITIVE roll, and it is not a typo that this disagrees in sign with the
    // pitch above.
    //
    // The two axes genuinely need opposite signs. Rotating about +X carries the
    // nose at +Z DOWN, so lifting a high nose takes -pitch. Rotating about +Z
    // carries +X UP, so lifting the high side takes +roll. Matching the signs
    // because they look like they should match is what put the chassis in a
    // mirror of its own hillside: leaning uphill on a slope that fell away
    // downhill, which is invisible driving straight up a hill and obvious the
    // moment you cross one. The dojo measured the error at twice the terrain's
    // tilt, which is exactly what a mirrored lean produces.
    this.body.rotateZ(roll);
    this.body.updateMatrix();

    // ── suspension ──────────────────────────────────────────────────────────
    // The wheels are CHILDREN of the body now, so they inherit its yaw, pitch
    // and roll for free and cannot drift out of alignment with it. A chassis
    // that twists independently of its own wheels is not a suspension, it is
    // two objects that happen to be near each other.
    //
    // What a rocker-bogie actually permits is vertical travel at each station
    // and nothing else, so that is the only freedom left here: x and z stay
    // pinned to the station, and only y moves.
    //
    // The ground is re-read UNDER THE DRAWN WHEEL rather than reused from the
    // contacts above. Those were sampled on a flat projection of the stations,
    // but the body is tilted, so a station 1.25 m out is drawn 1.25*cos(tilt)
    // out — and on a slope the ground there is not the ground that was read.
    // The error is invisible on the level and grows with tilt, which is exactly
    // the shape of "sometimes clips the ground".
    _inv.copy(this.body.matrix).invert();
    const nominalY = wr - 0.02;
    const travelLimit = wr * 1.8;
    // How much of the body's own up axis points at the sky. Moving a wheel by
    // one metre along body-local Y moves it this much in world Y.
    const upY = Math.max(0.2, _v.set(0, 1, 0).applyQuaternion(this.body.quaternion).y);
    for (let i = 0; i < st.length; i++) {
      const [lx, lz] = st[i]!;
      // Solved twice, and twice is the measured optimum.
      //
      // Moving the wheel along the tilted body's Y also moves it in X and Z, so
      // the first pass reads the ground a little to the side of where the wheel
      // ends up, and a correction pass fixes that. A THIRD pass is worse, not
      // better — on curved ground the sample point keeps moving and the
      // iteration chases it, which the dojo measured as the imported rover's
      // worst contact going from 0.19 m to 0.26. Damping the step recovered
      // some of that but never beat stopping at two.
      let travel = nominalY;
      for (let pass = 0; pass < 2; pass++) {
        const at = _v.set(lx, travel, lz).applyMatrix4(this.body.matrix);
        const g = Math.max(this.groundAt(at.x, at.z), this.rockTopAt(at.x, at.z));
        travel = THREE.MathUtils.clamp(
          travel + (g + wr - at.y) / upY, nominalY - travelLimit, nominalY + travelLimit);
      }

      const wheel = this.wheels[i];
      if (wheel) {
        const [bx, bz] = this.stations[i] ?? [lx, lz];
        wheel.position.set(bx, travel, bz);
        // Rolling is now purely local: the body already points where the rover
        // is going, so spinning about the wheel's own +X carries its top
        // forward.
        wheel.rotation.set(this.spin, 0, 0);

        // The arm from the rocker pivot down to this wheel, in body space.
        const hx = bx * 0.55;
        const hz = bz * 0.55;
        const hy = ROVER.deckM * 0.62;
        const dx = bx - hx;
        const dy = travel - hy;
        const dz = bz - hz;
        const len = Math.max(0.05, Math.hypot(dx, dy, dz));
        const arm = this.rockers[i]!;
        arm.position.set((bx + hx) / 2, (travel + hy) / 2, (bz + hz) / 2);
        arm.scale.set(1, len, 1);
        arm.quaternion.setFromUnitVectors(_up, _v.set(dx, dy, dz).normalize());
      }

      // The imported model's wheels ride the same suspension.
      //
      // Its frame is the asset's, not ours — different scale, and Y up but
      // otherwise unrelated — so the travel is converted through the holder's
      // scale rather than copied. Rolling is the same angle either way, since
      // an angle has no units.
      const mw = this.modelWheels[i];
      if (mw && this.model) {
        const k = this.model.children[0]?.scale.x || 1;
        const rest = this.modelWheelRest[i]!;
        mw.position.set(rest.x, rest.y + (travel - nominalY) / Math.max(k, 1e-6), rest.z);
        mw.rotation.set(this.spin, 0, 0);
      }
    }
  }

  /**
   * Swap in the imported Curiosity, loading it the first time it is asked for.
   *
   * The model arrives with no wheel nodes — it is split by material, so every
   * mesh spans the whole vehicle — and it used to ride as one rigid lump for
   * exactly that reason. It does not any more: extractWheels() cuts the six
   * wheels out geometrically and binds them to the same six stations the
   * hand-built rig uses, so this one rolls and articulates too. The borrowed
   * mesh drives on our suspension rather than being carried by it.
   *
   * The hand-built rover stays regardless. It is the readable one — you can see
   * the rocker arms move — where this is forty-eight thousand triangles of real
   * hardware.
   */
  async setSkin(skin: 'built' | 'curiosity'): Promise<void> {
    this.skin = skin;
    if (skin === 'curiosity' && !this.model) {
      const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
      const gltf = await new GLTFLoader().loadAsync(CURIOSITY_CREDIT.file);
      const m = gltf.scene;

      // Rebuild every material, pulling the diffuse map out of the extension
      // three refuses to read. parser.associations is the documented route from
      // a live Material back to its index in the glTF JSON.
      const parser = gltf.parser as unknown as {
        json: { materials?: Array<Record<string, any>> };
        associations: Map<object, { materials?: number }>;
        getDependency(kind: string, i: number): Promise<unknown>;
      };
      const swaps: Array<Promise<void>> = [];
      m.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh) return;
        const old = mesh.material as THREE.Material;
        const idx = parser.associations.get(old)?.materials;
        const def = idx !== undefined ? parser.json.materials?.[idx] : undefined;
        const sg = def?.['extensions']?.['KHR_materials_pbrSpecularGlossiness'];
        const texIdx = sg?.['diffuseTexture']?.['index']
          ?? def?.['pbrMetallicRoughness']?.['baseColorTexture']?.['index'];
        swaps.push((async () => {
          let map: THREE.Texture | null = null;
          if (texIdx !== undefined) {
            map = (await parser.getDependency('texture', texIdx)) as THREE.Texture;
            map.name = 'rover-albedo';
            map.colorSpace = THREE.SRGBColorSpace;
            map.flipY = false;
          }
          mesh.material = texturedMaterial(this.uniformsRef, map);
          old.dispose();
        })());
      });
      await Promise.all(swaps);

      // Normalise from the bounding box rather than trusting the file's units.
      // An imported mesh may be in centimetres, inches or whatever the author's
      // package defaulted to, and the one thing we actually know is that
      // Curiosity is three metres long — so measure it and scale to that.
      const box = new THREE.Box3().setFromObject(m);
      const size = new THREE.Vector3();
      const centre = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(centre);
      const longest = Math.max(size.x, size.z);
      // CURIOSITY_MODEL_SCALE of its true 3 m. Three metres is the honest
      // number and the reason the rover works as a scale reference at all —
      // this deliberately trades some of that away, because against boulders
      // running to several metres a truthful rover reads as a toy and stops
      // doing the job it is there for. One constant, easy to put back.
      const k = (longest > 1e-6 ? ROVER.lengthM / longest : 1) * CURIOSITY_MODEL_SCALE;
      m.scale.setScalar(k);
      // Sit its underside on y=0 and centre it over the axles, so it shares the
      // hand-built rover's origin and the physics does not care which is drawn.
      m.position.set(-centre.x * k, -box.min.y * k, -centre.z * k);

      // Cut the wheels loose so the imported model can articulate too.
      this.modelWheels = extractWheels(m);
      this.modelWheelRest = this.modelWheels.map((g) => g.position.clone());

      // Publish where those wheels ACTUALLY are, in body metres, so the physics
      // samples the ground under the vehicle being drawn.
      //
      // This model is three times life size and its wheelbase is a different
      // fraction of its length than ours: its axles sit about 2.7 m fore and
      // aft where ours sit at 1.25. Reading the terrain at our stations and
      // drawing wheels at its stations is two different vehicles sharing a
      // transform, and on a slope the gap showed as most of a metre of wheel
      // buried in the hill.
      this.modelStations = this.modelWheelRest.map((r) => [
        (r.x - centre.x) * k,
        (r.z - centre.z) * k,
      ] as [number, number]);
      // Tyre radius, measured the same way: the furthest any rim vertex sits
      // from the axle the wheel was re-centred on.
      const radii = this.modelWheels.map((g) => {
        let far = 0;
        g.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (!mesh.isMesh) return;
          const pa = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
          for (let i = 0; i < pa.count; i += 3) {
            const d = Math.hypot(pa.getY(i), pa.getZ(i));
            if (d > far) far = d;
          }
        });
        return far * k;
      }).filter((r) => r > 0).sort((a, b) => a - b);
      this.modelWheelRadius = radii.length ? radii[radii.length >> 1]! : 0;

      const holder = new THREE.Group();
      holder.add(m);
      // Longest axis is X in this asset; the rover drives along +Z.
      holder.rotation.y = size.x >= size.z ? Math.PI / 2 : 0;
      this.model = holder;
      this.body.add(holder);
    }
    this.rig.visible = skin === 'built';
    if (this.model) this.model.visible = skin === 'curiosity';
  }

  /**
   * Wander.
   *
   * Deliberately unhurried — a rover that darts about is a car, and the whole
   * point of this one is that ground takes time to cross. It holds a heading,
   * drifts off it slowly, and turns away from anything too steep to climb
   * rather than grinding against it, which is roughly how a real drive is
   * planned: pick a bearing, go until the terrain argues, pick another.
   */
  autoDrive(dt: number): RoverControls {
    this.autoTimer -= dt;
    if (this.autoTimer <= 0) {
      // A new bearing every 8-20 s, biased to gentle changes so it reads as
      // surveying rather than as indecision.
      this.autoTimer = 8 + Math.random() * 12;
      this.autoHeading = this.state.yaw + (Math.random() - 0.5) * 1.9;
    }

    // Look ahead; turn away from ground it cannot hold and from the plot edge.
    const look = 14;
    const hx = Math.sin(this.state.yaw);
    const hz = Math.cos(this.state.yaw);
    const ax = this.state.x + hx * look;
    const az = this.state.z + hz * look;
    let blocked = this.field.slopeAt(ax, az) > ROVER.slopeLimitDeg * 0.8;
    if (!blocked) {
      let inside = false;
      for (const p of this.field.placements) {
        if (this.field.insideHex(ax - p.x_m, az - p.z_m)) { inside = true; break; }
      }
      blocked = !inside;
    }
    if (blocked) {
      this.autoHeading = this.state.yaw + 1.6;
      this.autoTimer = Math.max(this.autoTimer, 3);
    }

    const d = wrapRad(this.autoHeading - this.state.yaw);
    return {
      throttle: 0.42,
      steer: THREE.MathUtils.clamp(d * 1.4, -1, 1),
      brake: false,
    };
  }

  /** Where a chase camera should look, in plot-local metres. */
  focus(out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.state.x, this.body.position.y + ROVER.mastM * 0.6, this.state.z);
  }

  // ── Survey system ──────────────────────────────────────────────────────

  /** Hand the rover the sub-plots it can survey by driving over them. */
  setSubPlots(plots: SubPlot[]): void {
    this.subPlots = plots;
    // Detect which cell the rover is currently in
    this.lastCellId = this.cellUnder()?.id ?? null;
  }

  /** Find which sub-plot the rover is currently standing in. */
  private cellUnder(): SubPlot | null {
    for (const p of this.subPlots) {
      if (insideHexZ(this.state.x - p.x, this.state.z - p.z, p.radius_m)) return p;
    }
    return null;
  }

  /**
   * Check whether the rover has crossed into a new cell and survey it.
   *
   * Called from update(). Both manual driving and auto-survey trigger this:
   * the odometer does not distinguish who is steering.
   */
  checkSurvey(): void {
    const cell = this.cellUnder();
    if (!cell) { this.lastCellId = null; return; }
    if (cell.id === this.lastCellId) return;
    this.lastCellId = cell.id;
    if (markSurveyed(cell.id)) {
      cell.dataTier = 2 as DataTier;
      this.onSurvey?.(cell.id, cell);
    }
  }

  /** Add a survey job. The rover drives to target and surveys cells it crosses. */
  enqueueSurvey(job: SurveyJob): void {
    this.surveyQueue.push(job);
  }

  /** True while the rover has survey work to do. */
  get surveying(): boolean {
    return this.surveyQueue.length > 0;
  }

  /**
   * Drive toward the current survey target and return the controls.
   *
   * When the rover arrives (within one cell radius), the job is popped and
   * the next one starts. If there is no job, returns null so the caller
   * falls back to its own input.
   */
  surveyDrive(dt: number): RoverControls | null {
    if (this.surveyQueue.length === 0) return null;
    const job = this.surveyQueue[0]!;
    const target = job.target;
    if (!target) {
      // "Survey plot" — wander until we run out of unsurveyed cells
      const unsurveyed = this.subPlots.find(
        (p) => p.dataTier < 2 && !isSurveyed(p.id),
      );
      if (!unsurveyed) {
        this.surveyQueue.shift();
        return null;
      }
      // Steer toward the nearest unsurveyed cell
      return this.driveToward(unsurveyed.x, unsurveyed.z, dt);
    }
    // Single cell target
    const dx = target.x - this.state.x;
    const dz = target.z - this.state.z;
    const dist = Math.hypot(dx, dz);
    if (dist < (this.subPlots[0]?.radius_m ?? 50)) {
      this.surveyQueue.shift();
      return null;
    }
    return this.driveToward(target.x, target.z, dt);
  }

  /** Steering controls to drive toward a world-space point. */
  private driveToward(tx: number, tz: number, _dt: number): RoverControls {
    const dx = tx - this.state.x;
    const dz = tz - this.state.z;
    const targetYaw = Math.atan2(dx, dz);
    const d = wrapRad(targetYaw - this.state.yaw);
    return {
      throttle: 0.55,
      steer: THREE.MathUtils.clamp(d * 2.0, -1, 1),
      brake: false,
    };
  }

  /**
   * Apply survey data to the sub-plots.
   *
   * Called once when the rover is deployed, to mark any cells that were
   * surveyed in a previous session. The landing cell is also surveyed.
   */
  applySurveyState(): void {
    for (const p of this.subPlots) {
      if (isSurveyed(p.id)) {
        p.dataTier = 2 as DataTier;
      }
    }
    // The landing cell is always surveyed
    const landing = this.cellUnder();
    if (landing && markSurveyed(landing.id)) {
      landing.dataTier = 2 as DataTier;
      this.onSurvey?.(landing.id, landing);
    }
    this.lastCellId = landing?.id ?? null;
  }

  /** Cast onto the cascades at the SURFACE band only - src/atlas.ts flips this
   *  at the regime edge. The rover never receives: only the terrain does. */
  setCastShadow(on: boolean): void {
    this.group.traverse((o) => { if (o instanceof THREE.Mesh) o.castShadow = on; });
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
  }
}
